import React, { createContext, useState, useContext, useEffect, ReactNode } from 'react';
import { authAPI, getApiBaseUrl, authStorage } from '../services/api';
import { User } from '../types';

// Resolve avatar URL to full backend URL
const resolveAvatarUrl = (avatar?: string | null): string | undefined => {
  if (!avatar) return undefined;
  if (avatar.startsWith('http')) return avatar; // Already absolute
  // Backend serves uploads at origin root, e.g. /uploads/avatars/...
  const apiBase = getApiBaseUrl(); // e.g. http://localhost:5000/api
  const origin = apiBase.replace(/\/api$/, ''); // e.g. http://localhost:5000
  return `${origin}${avatar}`;
};

interface AuthContextType {
  user: User | null;
  loading: boolean;
  login: (email: string, password: string, remember?: boolean) => Promise<{ success: boolean; error?: string }>;
  register: (userData: any) => Promise<{ success: boolean; error?: string }>;
  loginWithLinkedIn: () => Promise<void>;
  loginWithLinkedInCallback: (code: string, state: string) => Promise<{ success: boolean; error?: string }>;
  logout: () => void;
  refreshUser: () => Promise<void>;
  isAuthenticated: boolean;
  isAdmin: boolean;
  isSuperAdmin: boolean;
  isProjectManager: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = authStorage.getToken();
    const storedUser = authStorage.getRawUser();

    if (token && storedUser) {
      try {
        const parsedUser = JSON.parse(storedUser);
        setUser(parsedUser);

        // Verify token is still valid and map to frontend User type
        authAPI.getCurrentUser()
          .then((userData) => {
            const fullName = `${userData.firstName || ''} ${userData.lastName || ''}`.trim();
            const initials = `${userData.firstName?.[0] || ''}${userData.lastName?.[0] || ''}`.toUpperCase();
            const mapped: User = {
              id: userData.id || userData._id,
              email: userData.email,
              name: fullName || userData.name || userData.email,
              initials: initials || userData.initials,
              role: userData.role,
              company: userData.company?.name || userData.company || '',
              avatar: resolveAvatarUrl(userData.avatar),
            };
            setUser(mapped);
            authStorage.setUser(mapped);
          })
          .catch(() => {
            // Token is invalid, clear everything
            authStorage.clear();
            setUser(null);
          })
          .finally(() => setLoading(false));
      } catch (error) {
        console.error('Failed to parse stored user:', error);
        authStorage.clear();
        setLoading(false);
      }
    } else {
      setLoading(false);
    }
  }, []);

  const login = async (email: string, password: string, remember: boolean = true) => {
    try {
      const response = await authAPI.login(email, password, remember);

      // Map backend user to frontend User type
      const fullName = `${response.user.firstName} ${response.user.lastName}`;
      const initials = `${response.user.firstName?.[0] || ''}${response.user.lastName?.[0] || ''}`.toUpperCase();

      const mappedUser: User = {
        id: response.user.id || response.user._id,
        email: response.user.email,
        name: fullName,
        initials: initials,
        role: response.user.role,
        company: response.user.company?.name || 'Unknown Company',
        avatar: resolveAvatarUrl(response.user.avatar),
      };

      setUser(mappedUser);
      authStorage.setUser(mappedUser);

      return { success: true };
    } catch (error: any) {
      const message = error.response?.data?.error || 'Login failed';
      return { success: false, error: message };
    }
  };

  const register = async (userData: any) => {
    try {
      const response = await authAPI.register(userData);

      // Map backend user to frontend User type
      const fullName = `${response.user.firstName} ${response.user.lastName}`;
      const initials = `${response.user.firstName?.[0] || ''}${response.user.lastName?.[0] || ''}`.toUpperCase();

      const mappedUser: User = {
        id: response.user.id || response.user._id,
        email: response.user.email,
        name: fullName,
        initials: initials,
        role: response.user.role,
        company: response.user.company?.name || userData.companyName,
        avatar: resolveAvatarUrl(response.user.avatar),
      };

      setUser(mappedUser);
      authStorage.setUser(mappedUser);

      return { success: true };
    } catch (error: any) {
      const message = error.response?.data?.error || 'Registration failed';
      return { success: false, error: message };
    }
  };

  const loginWithLinkedIn = async () => {
    try {
      const data = await authAPI.getLinkedInAuthUrl();
      sessionStorage.setItem('linkedin_oauth_state', data.state);
      window.location.href = data.authorizationUrl;
    } catch (error) {
      console.error('Failed to initiate LinkedIn login:', error);
    }
  };

  const loginWithLinkedInCallback = async (code: string, state: string) => {
    try {
      const response = await authAPI.linkedInCallback(code, state);

      const fullName = `${response.user.firstName} ${response.user.lastName}`;
      const initials = `${response.user.firstName?.[0] || ''}${response.user.lastName?.[0] || ''}`.toUpperCase();

      const mappedUser: User = {
        id: response.user.id || response.user._id,
        email: response.user.email,
        name: fullName,
        initials,
        role: response.user.role,
        company: response.user.company?.name || 'Onboarding',
        avatar: resolveAvatarUrl(response.user.avatar),
      };

      setUser(mappedUser);
      authStorage.setUser(mappedUser);

      return { success: true };
    } catch (error: any) {
      const message = error.response?.data?.error || 'LinkedIn login failed';
      return { success: false, error: message };
    }
  };

  const logout = () => {
    authAPI.logout();
    setUser(null);
    window.location.href = '/login';
  };

  const refreshUser = async () => {
    try {
      const response = await authAPI.getCurrentUser();
      const fullName = `${response.firstName || ''} ${response.lastName || ''}`.trim();
      const initials = `${response.firstName?.[0] || ''}${response.lastName?.[0] || ''}`.toUpperCase();

      const mappedUser: User = {
        id: response.id || response._id,
        email: response.email,
        name: fullName || response.name || response.email,
        initials: initials || response.initials,
        role: response.role,
        company: response.company?.name || response.company || '',
        avatar: resolveAvatarUrl(response.avatar),
      };

      setUser(mappedUser);
      authStorage.setUser(mappedUser);
    } catch (error) {
      console.error('Failed to refresh user:', error);
    }
  };

  const value: AuthContextType = {
    user,
    loading,
    login,
    register,
    loginWithLinkedIn,
    loginWithLinkedInCallback,
    logout,
    refreshUser,
    isAuthenticated: !!user,
    isAdmin: user?.role === 'admin' || user?.role === 'superAdmin',
    isSuperAdmin: user?.role === 'superAdmin',
    isProjectManager: user?.role === 'projectManager' || user?.role === 'admin' || user?.role === 'superAdmin',
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};